import {motion} from 'framer-motion';
import {Box} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {useRef} from 'react';

import {buildArray, RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {LightboxAction} from '../types/LightboxAction';
import {LightboxImage} from '../types/LightboxImage';
import {ZoomRef} from '../types/ZoomRef';
import {Actions} from './Actions';
import {Zoom} from './Zoom';
import {ZoomImage} from './ZoomImage';

interface LightboxSlideDetailProps extends RequiredTestIdProps {
  image: LightboxImage;
  onDetailClose: () => void;
}

const detailVariants = cva(
  'absolute inset-0 z-[var(--z-lightbox-detail)] flex h-full w-full items-center justify-center bg-[var(--color-palettes-black-10-100)]'
);

export function Detail(props: LightboxSlideDetailProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ZoomRef>(null);

  useOnMount(() => {
    if (wrapperRef.current) {
      wrapperRef.current.focus(); // Focus wrapper, so user can focus the buttons by pressing tab
    }
  });

  const onZoomIn = () => {
    if (zoomRef.current) {
      zoomRef.current.zoomIn();
    }
  };

  const onZoomOut = () => {
    if (zoomRef.current) {
      zoomRef.current.zoomOut();
    }
  };

  const actions = props.image.actions ?? [];

  const zoomControl = buildArray<LightboxAction>([])
    .add({
      id: 'zoomOutBtn',
      icon: 'lightbox/minus',
      onClick: () => onZoomOut(),
    })
    .add({
      id: 'zoomInBtn',
      icon: 'lightbox/plus',
      onClick: () => onZoomIn(),
    });

  return (
    <motion.div
      className={cn(detailVariants())}
      initial={{opacity: 0, scale: 0.95}}
      animate={{opacity: 1, scale: 1}}
      exit={{opacity: 0, scale: 0.95}}
      tabIndex={-1} // Focus programmatically only
      ref={wrapperRef}
    >
      <Box width="100%" height="100%" position="relative">
        <Zoom
          ref={zoomRef}
          data-testid={suffixTestId('imageDetail', props)}
          renderSlide={(ref) => (
            <ZoomImage
              ref={ref}
              data-testid={suffixTestId('imageDetail', props)}
              image={props.image}
            />
          )}
        />
        <Actions
          data-testid={suffixTestId('imageDetail', props)}
          zIndex="LIGHTBOX_DETAIL_ACTIONS"
          actionPosition={{
            top: 4,
            right: 15,
            left: 'auto',
          }}
          actions={[actions, zoomControl]}
          isActive
        />
        <Actions
          data-testid={suffixTestId('imageDetail', props)}
          actionPosition={{top: 4, right: 4, left: 'auto'}}
          zIndex="LIGHTBOX_DETAIL_ACTIONS"
          actions={buildArray<LightboxAction[]>().add([
            {
              icon: 'navigation/close',
              onClick: props.onDetailClose,
              id: 'closeBtn',
            },
          ])}
          isActive
        />
      </Box>
    </motion.div>
  );
}
